import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { fetchAgreements, fetchAgreementTemplate, updateAgreementTemplate } from "@/lib/api";
import { formatDateTime } from "@/lib/constants";
import { getErrorMessage } from "@/lib/utils";

const STATUS_COLORS = {
  DRAFT: "bg-slate-100 text-slate-600",
  SENT: "bg-amber-100 text-amber-700",
  VIEWED: "bg-foreground/5 text-foreground/70",
  SIGNED: "bg-emerald-100 text-emerald-700",
};

export default function AgreementsPage() {
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [template, setTemplate] = useState("");
  const [templateAccessDenied, setTemplateAccessDenied] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const loadAgreements = useCallback(async () => {
    setLoading(true);
    try {
      const next = await fetchAgreements(filter !== "ALL" ? { status: filter } : {});
      setAgreements(next);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to load agreements"));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadTemplate = useCallback(async () => {
    setTemplateLoading(true);
    try {
      const data = await fetchAgreementTemplate();
      setTemplate(data.agreement_terms_template || "");
      setTemplateAccessDenied(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setTemplateAccessDenied(true);
      } else {
        toast.error(getErrorMessage(err, "Failed to load agreement terms"));
      }
    } finally {
      setTemplateLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgreements();
  }, [loadAgreements]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleTemplateSave = async () => {
    if (!template.trim()) {
      toast.error("Agreement terms template cannot be empty");
      return;
    }

    setTemplateSaving(true);
    try {
      const updated = await updateAgreementTemplate({ agreement_terms_template: template.trim() });
      setTemplate(updated.agreement_terms_template || template.trim());
      toast.success("Agreement terms template updated");
    } catch (err) {
      if (err?.response?.status === 403) {
        setTemplateAccessDenied(true);
        toast.error("Only owners and managers can update the agreement template");
      } else {
        toast.error(getErrorMessage(err, "Failed to update agreement terms"));
      }
    } finally {
      setTemplateSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto" data-testid="agreements-page">
      <div className="max-w-6xl mx-auto p-6 lg:p-8 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">Agreements</h1>
            <p className="text-sm text-slate-500 mt-1 font-medium">Track agreement progress and manage the default terms for future agreements.</p>
          </div>
          <button
            type="button"
            data-testid="open-agreement-template-modal"
            onClick={() => setTemplateDialogOpen(true)}
            className="inline-flex items-center gap-2 h-11 px-5 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            <FloppyDisk weight="bold" className="w-4 h-4" />
            Edit Terms Template
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {["ALL", "DRAFT", "SENT", "VIEWED", "SIGNED"].map((s) => (
            <button
              key={s}
              data-testid={`agreement-filter-${s.toLowerCase()}`}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${filter === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              {s === "ALL" ? "All" : s}
            </button>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-400">Loading...</div>
          ) : agreements.length === 0 ? (
            <div className="p-8 text-center space-y-3">
              <p className="text-sm font-semibold text-slate-700">No agreements found.</p>
              <p className="text-sm text-slate-400">Generate and send an agreement from a draft rental to start tracking signature progress here.</p>
              <button
                type="button"
                onClick={() => navigate("/rentals")}
                className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50"
              >
                View Rentals
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="agreements-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 whitespace-nowrap">
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Rental</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Customer</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Sent</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Signed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {agreements.map((agreement) => (
                    <tr
                      key={agreement.id}
                      data-testid={`agreement-row-${agreement.rental_number}`}
                      onClick={() => navigate(`/rentals/${agreement.rental_id}`)}
                      className="hover:bg-slate-50 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <td className="px-4 py-3 font-mono font-bold text-foreground">{agreement.rental_number}</td>
                      <td className="px-4 py-3 font-semibold text-slate-800">{agreement.customer_name || agreement.customer_email || "Customer pending"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${STATUS_COLORS[agreement.status] || ""}`}>{agreement.status}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{agreement.sent_at ? formatDateTime(agreement.sent_at) : "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{agreement.signed_at ? formatDateTime(agreement.signed_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Agreement Terms Template</DialogTitle>
            <DialogDescription>
              Updating this changes the default terms used for newly generated agreements. Existing agreements stay unchanged.
            </DialogDescription>
          </DialogHeader>

          {templateLoading ? (
            <div className="text-sm text-slate-400 py-8">Loading template...</div>
          ) : (
            <>
              <div className="space-y-3 overflow-y-auto pr-1">
                <textarea
                  data-testid="agreement-template-input"
                  rows={18}
                  value={template}
                  onChange={(event) => setTemplate(event.target.value)}
                  className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl resize-y bg-white font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black"
                  placeholder="Enter the standard terms and conditions you want new agreements to use..."
                />
                <p className="text-xs text-slate-500">
                  Agreement details like rental number, vehicle, customer, dates, and pricing are still injected automatically above this template.
                </p>
                {templateAccessDenied && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    You can view the default template here, but only owners and managers can update it.
                  </div>
                )}
              </div>
              <DialogFooter>
                <button
                  type="button"
                  onClick={() => setTemplateDialogOpen(false)}
                  className="inline-flex items-center justify-center h-11 px-5 rounded-md border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  Close
                </button>
                <button
                  type="button"
                  data-testid="agreement-template-save"
                  onClick={handleTemplateSave}
                  disabled={templateSaving || templateAccessDenied}
                  className="inline-flex items-center gap-2 h-11 px-5 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  <FloppyDisk weight="bold" className="w-4 h-4" />
                  {templateSaving ? "Saving..." : "Save Template"}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
